import React, { useContext, useState } from 'react';

import IconCommentLink from '@digital-hig/icon/icons/comment';
import Modal from '@digital-hig/mui/Modal';
import SvgIcon from '@digital-hig/mui/SvgIcon';
import { useTypedTranslation } from 'auonline2-next/lib/useTypedTranslation';
import cn from 'classnames';

import { POST_ITEMS_PER_PAGE } from '../../../apis/khoros/constants';
import { useKhorosPosts, useKhorosTopic } from '../../../apis/khoros/hooks';
import Comments from '../../../components/presentational/comments/Comments';
import Skeleton from '../../../components/presentational/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipInfoContent,
  TooltipTrigger,
} from '../../../components/presentational/tooltip/Tooltip';
import extractErrorMessage from '../../../helpers/extractErrorMessage';
import { ConfigContext } from '../../contexts/ConfigContext';

interface CommentProps {
  topicId: string;
  className?: string | object;
  userContentURL?: string;
  userProfileURL?: string;
  baseURL?: string;
}

export default function Comment({
  topicId,
  className,
  userContentURL,
  userProfileURL,
  baseURL,
}: CommentProps) {
  let count: number | null = null;

  const { t } = useTypedTranslation(['common']);
  const { KHOROS_API } = useContext(ConfigContext);

  const topicProps = {
    baseURL: baseURL || KHOROS_API,
    topicId,
  };

  const { data: topicContent, isLoading: isTopicLoading } =
    useKhorosTopic(topicProps);

  if (topicContent) {
    count = topicContent.kh_total_replies_count;
  }

  // Call for API requests before opening the modal to have everything pre-loaded
  const { isLoading, error, isError } = useKhorosPosts({
    ...topicProps,
    pageSize: POST_ITEMS_PER_PAGE,
  });

  const [open, setOpen] = useState(false);
  const [displayError, setDisplayError] = useState(false);

  const closeModal = () => setOpen(false);

  const openModal = () => setOpen(true);

  const openErrorTooltip = () => setDisplayError(true);

  const closeErrorTooltip = () => setDisplayError(false);

  return (
    <>
      <Tooltip open={displayError} onOpenChange={closeErrorTooltip}>
        {isLoading ? (
          <Skeleton width="100px" height="20px" />
        ) : (
          <TooltipTrigger
            type="button"
            onClick={isError ? openErrorTooltip : openModal}
            className={cn(
              'au-flex au-items-center au-gap-c10 hover:au-text-adskTurquoise50 au-cursor-pointer au-gap-c10',
              className
            )}
          >
            <SvgIcon fontSize="large" className="au-w-c20 au-h-c20">
              <IconCommentLink color="white" />
            </SvgIcon>
            <div
              className={cn(
                'au-text-c18 au-leading-large au-text-gray-600 au-font-element au-text-white'
              )}
            >
              <span>{t('comments.comment', { ns: 'common' })}</span>
              {count ? ` (${count})` : null}
            </div>
          </TooltipTrigger>
        )}
        <TooltipContent>
          <TooltipInfoContent>
            {extractErrorMessage(error) ||
              t('comments.loadError', { ns: 'common' })}
          </TooltipInfoContent>
        </TooltipContent>
      </Tooltip>
      <Modal
        handleClose={closeModal}
        open={open}
        title=""
        maxWidth={false}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '580px',
              backgroundColor: '#161616',
              color: '#fff',
            },
            '& .MuiSvgIcon-root': {
              color: '#fff',
              zIndex: 10,
            },
          },
        }}
        content={
          isTopicLoading || isLoading ? (
            <div className="au-flex au-space-x-4">
              <div>
                <Skeleton width={50} height={50} rounded />
              </div>
              <div className="au-max-w-[500px] au-flex-1 au-gap-y-4">
                <Skeleton count={3} width="100%" height="20px" />
              </div>
            </div>
          ) : (
            <div className="au-h-full au-overflow-y-auto au-pt-c40">
              <Comments
                commentCount={count}
                baseURL={baseURL}
                userContentURL={userContentURL}
                userProfileURL={userProfileURL}
                topicId={topicId}
              />
            </div>
          )
        }
      />
    </>
  );
}

Comment.defaultProps = {};
Comment.propTypes = {};
Comment.attributes = {
  topicId: {
    type: 'string',
    default: '',
  },
  baseURL: {
    type: 'string',
    defaul: '',
  },
  userContentURL: {
    type: 'string',
    default: '',
  },
  userProfileURL: {
    type: 'string',
    default: '',
  },
};
